import * as Sentry from '@sentry/react'

/**
 * Safely access CSS rules from a stylesheet, handling CORS errors
 * @param sheet - The CSSStyleSheet to access
 * @returns Array of CSS rules or empty array if CORS error
 */
export const safeGetCssRules = (sheet: CSSStyleSheet): CSSRule[] => {
  try {
    return Array.from(sheet.cssRules || sheet.rules || [])
  } catch (e) {
    // CORS error - stylesheet is from different origin
    // Log to Sentry but don't break the app
    if (e instanceof DOMException && e.name === 'SecurityError') {
      Sentry.captureMessage('CORS error accessing stylesheet rules', {
        level: 'warning',
        tags: {
          errorType: 'css_cors_access',
        },
        extra: {
          href: sheet.href || 'unknown',
        },
      })
    }
    return []
  }
}

/**
 * Initialize HTML validation and error detection
 * Catches HTML parsing errors and validates HTML structure to prevent corrupted HTML issues
 */
export const initHtmlValidation = () => {
  // Catch CORS errors when accessing CSS rules from cross-origin stylesheets
  // This commonly happens with external stylesheets (Google Fonts, Font Awesome, etc.)
  const originalError = window.onerror
  window.onerror = (message, source, lineno, colno, error) => {
    // Check for CSS CORS errors
    if (
      typeof message === 'string' &&
      (message.includes('cssRules') ||
        message.includes('Cannot access rules') ||
        message.includes("Failed to read the 'cssRules' property"))
    ) {
      // This is a CORS error with stylesheets - log but don't break the app
      Sentry.captureMessage('CSS CORS error detected', {
        level: 'warning',
        tags: {
          errorType: 'css_cors_error',
          platform: navigator.platform,
          userAgent: navigator.userAgent,
        },
        extra: {
          message: String(message),
          source,
          lineno,
          colno,
        },
      })
      // Return true to prevent default error handling (don't break the app)
      return true
    }
    // Call original error handler if provided
    if (originalError) {
      return originalError(message, source, lineno, colno, error)
    }
    return false
  }

  // Also catch unhandled promise rejections that might be related
  window.addEventListener('unhandledrejection', (event) => {
    if (
      event.reason?.message?.includes('cssRules') ||
      event.reason?.message?.includes('Cannot access rules')
    ) {
      Sentry.captureMessage('CSS CORS error in promise rejection', {
        level: 'warning',
        tags: {
          errorType: 'css_cors_error',
          platform: navigator.platform,
        },
        extra: {
          reason: event.reason?.message || String(event.reason),
        },
      })
      // Prevent the error from breaking the app
      event.preventDefault()
    }
  })

  // Catch HTML parsing errors and report to Sentry
  window.addEventListener('error', (event) => {
    // Check if it's an HTML parsing error
    if (
      event.error?.name === 'SyntaxError' &&
      event.message?.includes('Unexpected token')
    ) {
      const isHtmlError =
        event.filename === window.location.href || event.lineno === 1
      const isJsFileReturningHtml =
        event.filename?.endsWith('.js') ||
        event.filename?.includes('/static/js/') ||
        (event.message?.includes("Unexpected token '<'") &&
          event.filename !== window.location.href)

      if (isHtmlError || isJsFileReturningHtml) {
        // Check if a JS file is returning HTML (common 404 issue)
        const errorType = isJsFileReturningHtml
          ? 'js_file_returning_html'
          : 'html_parsing_error'

        Sentry.captureException(event.error, {
          tags: {
            errorType,
            platform: navigator.platform,
            userAgent: navigator.userAgent,
          },
          extra: {
            message: event.message,
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            htmlSnippet: document.documentElement.outerHTML.substring(0, 500),
            // Try to get the actual response if it's a JS file issue
            ...(isJsFileReturningHtml && {
              expectedType: 'application/javascript',
              actualContent: event.message,
            }),
          },
        })
      }
    }
  })

  // Validate HTML structure on load
  if (typeof document !== 'undefined') {
    try {
      // Basic HTML validation
      if (!document.documentElement || !document.body) {
        throw new Error(
          'Invalid HTML structure: missing documentElement or body'
        )
      }

      // Check for common HTML corruption patterns
      const htmlContent = document.documentElement.outerHTML
      if (
        htmlContent.includes('Unexpected token') ||
        htmlContent.match(/<meta[^>]*else/i) // Check for "else" in meta tags
      ) {
        Sentry.captureMessage('Corrupted HTML detected', {
          level: 'error',
          tags: {
            errorType: 'html_corruption',
            platform: navigator.platform,
          },
          extra: {
            htmlSnippet: htmlContent.substring(0, 1000),
          },
        })
      }
    } catch (e) {
      Sentry.captureException(e, {
        tags: {
          errorType: 'html_validation_error',
        },
      })
    }
  }
}
